import { useEffect, useRef, useState } from 'react';

/**
 * Auto-logout user after IDLE_MS of inactivity. Shows a warning dialog
 * WARNING_MS before the actual logout so the user can extend the session.
 *
 * Activity = any of: mouse move, click, key press, scroll, touch.
 * Activity in any browser tab counts (via storage event sync).
 */
const IDLE_MS = 5 * 60 * 1000;     // 5 minutes total inactivity → logout
const WARNING_MS = 30 * 1000;       // show warning 30s before logout
const ACTIVITY_KEY = 'lastActivityAt';

export default function useIdleLogout(isAuthenticated, onLogout) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_MS / 1000));
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);

  const clearAll = () => {
    if (warnTimer.current) { clearTimeout(warnTimer.current); warnTimer.current = null; }
    if (logoutTimer.current) { clearTimeout(logoutTimer.current); logoutTimer.current = null; }
    if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
  };

  const resetTimers = () => {
    clearAll();
    setShowWarning(false);
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(Math.floor(WARNING_MS / 1000));
      // Update countdown each second so the modal shows live timer
      countdownTimer.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
    }, IDLE_MS - WARNING_MS);

    logoutTimer.current = setTimeout(() => {
      clearAll();
      setShowWarning(false);
      onLogout?.();
    }, IDLE_MS);
  };

  const stayActive = () => {
    resetTimers();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      clearAll();
      setShowWarning(false);
      return;
    }

    // Activity listeners — throttle mousemove so we don't reset on every pixel.
    let lastActivity = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity < 1000) return;  // throttle to 1Hz
      lastActivity = now;
      // Only reset if the warning modal is NOT showing — once warning shows,
      // user MUST explicitly click "Nastavi sesiju" (otherwise an accidental
      // mouse-bump on a locked screen would silently keep them logged in).
      if (!warnTimer.current && logoutTimer.current) {
        // already in warning phase — ignore raw activity
        return;
      }
      resetTimers();
    };

    // Sync activity across multiple tabs of the same user
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) {
        // Other tab had activity — extend this tab's timer too
        if (!showWarning) resetTimers();
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);

    resetTimers();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { showWarning, secondsLeft, stayActive };
}
